import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { client } from '../../utils/client'
import querystring from 'querystring'
import { DistributionType } from 'src/utils/enum'

// Actions - used with useDispatch
export const itemSlice = createSlice({
  name: 'item',
  initialState: {
    items: null,
    pagination: null,
    getItems: {
      status: null,
      error: null,
    },
    createItem: {
      status: null,
      error: null,
    },
    updateItem: {
      status: null,
      error: null,
    },
    setItemActiveStatus: {
      status: null,
      error: null,
    },
    delistItem: {
      status: null,
      error: null,
    },
    getItemsForOrdering: {
      status: null,
      error: null,
      data: null,
    },
    updateItemOrder: {
      status: null,
      error: null,
    },
  },
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        // Clear status and error
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getItems
      .addCase(getItems.pending, (state, action) => {
        state.getItems.status = 'loading'
      })
      .addCase(getItems.fulfilled, (state, action) => {
        state.getItems.status = 'succeeded'
        state.getItems.error = null // Clear any previous errors

        // Store items info
        if (action.payload.data.success) {
          state.items = action.payload.data.data
          state.pagination = action.payload.data.pagination
        }
      })
      .addCase(getItems.rejected, (state, action) => {
        state.getItems.status = 'failed'
        state.getItems.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        state.items = null
      })
      // createItem
      .addCase(createItem.pending, (state, action) => {
        state.createItem.status = 'loading'
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.createItem.status = 'succeeded'
        state.createItem.error = null // Clear any previous errors

        // Store a new item info
        if (action.payload.data.success) {
          state.items.push(action.payload.data.data)
        }
      })
      .addCase(createItem.rejected, (state, action) => {
        state.createItem.status = 'failed'
        state.createItem.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // updateItem
      .addCase(updateItem.pending, (state, action) => {
        state.updateItem.status = 'loading'
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.updateItem.status = 'succeeded'
        state.updateItem.error = null // Clear any previous errors
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.updateItem.status = 'failed'
        state.updateItem.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // setItemActiveStatus
      .addCase(setItemActiveStatus.pending, (state, action) => {
        state.setItemActiveStatus.status = 'loading'
      })
      .addCase(setItemActiveStatus.fulfilled, (state, action) => {
        state.setItemActiveStatus.status = 'succeeded'
        state.setItemActiveStatus.error = null // Clear any previous errors

        // Reflect updates
        if (action.payload.data.success) {
          const itemIndex = state.items.findIndex(
            (item) => item.sysSku === action.payload.data.data.sysSku,
          )

          if (itemIndex !== -1) {
            // Create a new object with the updated data
            const updatedItem = { ...state.items[itemIndex], ...action.payload.data.data }

            // Create a new array with the updated object
            const updatedItems = [...state.items]
            updatedItems[itemIndex] = updatedItem

            // Update the state
            state.items = updatedItems
          }
        }
      })
      .addCase(setItemActiveStatus.rejected, (state, action) => {
        state.setItemActiveStatus.status = 'failed'
        state.setItemActiveStatus.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // delistItem
      .addCase(delistItem.pending, (state, action) => {
        state.delistItem.status = 'loading'
      })
      .addCase(delistItem.fulfilled, (state, action) => {
        state.delistItem.status = 'succeeded'
        state.delistItem.error = null // Clear any previous errors

        // Reflect updates
        if (action.payload.data.success) {
          const itemIndex = state.items.findIndex(
            (item) => item.sysSku === action.payload.data.data.sysSku,
          )

          if (itemIndex !== -1) {
            // Create a new object with the updated data
            const delistedItem = { ...state.items[itemIndex], ...action.payload.data.data }

            // Create a new array with the updated object
            const updatedItems = [...state.items]
            updatedItems[itemIndex] = delistedItem

            // Update the state
            state.items = updatedItems
          }
        }
      })
      .addCase(delistItem.rejected, (state, action) => {
        state.delistItem.status = 'failed'
        state.delistItem.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // updateItemOrder
      .addCase(updateItemOrder.pending, (state, action) => {
        state.updateItemOrder.status = 'loading'
      })
      .addCase(updateItemOrder.fulfilled, (state, action) => {
        state.updateItemOrder.status = 'succeeded'
        state.updateItemOrder.error = null // Clear any previous errors
      })
      .addCase(updateItemOrder.rejected, (state, action) => {
        state.updateItemOrder.status = 'failed'
        state.updateItemOrder.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // getItemsForOrdering
      .addCase(getItemsForOrdering.pending, (state, action) => {
        state.getItemsForOrdering.status = 'loading'
      })
      .addCase(getItemsForOrdering.fulfilled, (state, action) => {
        state.getItemsForOrdering.status = 'succeeded'
        state.getItemsForOrdering.data = action.payload.data.data
        state.getItemsForOrdering.error = null
      })
      .addCase(getItemsForOrdering.rejected, (state, action) => {
        state.getItemsForOrdering.status = 'failed'
        state.getItemsForOrdering.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
  },
})
export const { clearStatus } = itemSlice.actions

// Async API (Thunks) - used with useDispatch
export const getItems = createAsyncThunk(
  'item/getItems',
  async (
    {
      sysSku,
      sku,
      shopUsername,
      branchCode,
      page,
      limit,
      sortBy,
      sortDirection,
      active,
      latest,
      approve,
      reject,
      delist,
      tags,
      itemCategory,
      serviceType,
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      const state = getState()
      if (state.branch) {
        const queryParams = new URLSearchParams({
          ...(sysSku && { sysSku }),
          ...(sku && { sku }),
          ...(shopUsername && { shopUsername }),
          ...(branchCode && { branchCode }),
          ...(Array.isArray(tags) && tags.length > 0 && { tags: tags }),
          ...(itemCategory && { itemCategory }),
          ...(serviceType && { serviceType }),
          page: page || 1,
          limit: limit || 10,
          ...(sortBy && { sortBy }),
          ...(sortDirection && { sortDirection }),
          ...(active !== undefined && { active }),
          ...(latest !== undefined && { latest }),
          ...(approve !== undefined && { approve }),
          ...(delist !== undefined && { delist }),
          ...(reject !== undefined && { reject }),
        })
        const url = `/shop/branches/${
          state.branch.currentSelectedBranchCode
        }/items?${queryParams.toString()}`
        const response = await client.get(url)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// createItem
export const createItem = createAsyncThunk(
  'item/createItem',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        // before sending to API
        // group.name need to convert to groupName
        // group.value need to convert to groupValue
        // timeParams.slotLength need to convert to timeSlotLength
        payload.groupName = payload.group.name
        payload.groupValue = payload.group.value
        payload.groupUnit = payload.group.unit
        // distributionType will be set to "TimeSlot" when itemCategory is "Washing" AND group.name is "BasketSize"
        // otherwise, it will be set to "Availability" (for generic services, DryCleaning, Supply/Finishing items)
        payload.distributionType =
          payload.itemCategory === 'Washing' && payload.group?.name === 'BasketSize'
            ? DistributionType.TIMESLOT
            : DistributionType.AVAILABILITY

        // Conditionally send timeSlotLength or capacity based on distributionType
        if (payload.distributionType === DistributionType.TIMESLOT) {
          payload.timeSlotLength = payload.timeParams?.slotLength
        } else if (payload.distributionType === DistributionType.AVAILABILITY) {
          payload.capacity = payload.capacityParams?.capacity
        }
        // Always send timeSpan
        payload.timeSpan = payload.timeSpan
        // Always send serviceType if it exists
        if (payload.serviceType) {
          payload.serviceType = payload.serviceType
        }

        // Remove countAsPackage from payload - backend will auto-set based on itemCategory
        delete payload.countAsPackage

        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/items`
        const response = await client.post(url, payload)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// updateItem
export const updateItem = createAsyncThunk(
  'item/updateItem',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        // before sending to API
        // group.name need to convert to groupName
        // group.value need to convert to groupValue
        // timeParams.slotLength need to convert to timeSlotLength
        payload.groupName = payload.group.name
        payload.groupValue = payload.group.value
        payload.groupUnit = payload.group.unit
        // distributionType will be set to "TimeSlot" when itemCategory is "Washing" AND group.name is "BasketSize"
        // otherwise, it will be set to "Availability" (for generic services, DryCleaning, Supply/Finishing items)
        payload.distributionType =
          payload.itemCategory === 'Washing' && payload.group?.name === 'BasketSize'
            ? DistributionType.TIMESLOT
            : DistributionType.AVAILABILITY

        // Conditionally send timeSlotLength or capacity based on distributionType
        if (payload.distributionType === DistributionType.TIMESLOT) {
          payload.timeSlotLength = payload.timeParams?.slotLength
        } else if (payload.distributionType === DistributionType.AVAILABILITY) {
          payload.capacity = payload.capacityParams?.capacity
        }
        // Always send timeSpan
        payload.timeSpan = payload.timeSpan

        // Remove countAsPackage from payload - backend will auto-set based on itemCategory
        delete payload.countAsPackage

        // Use encodeURIComponent to handle SKUs with special chars: /, spaces, (, ), Thai, etc.
        const encodedSku = encodeURIComponent(payload.sku)
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/items/${encodedSku}`
        const response = await client.put(url, payload)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Set item active status
export const setItemActiveStatus = createAsyncThunk(
  'item/setItemActiveStatus',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const encodedSku = encodeURIComponent(payload.sku)
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/items/${encodedSku}/set-active`
        const response = await client.post(url, payload)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// delistItem
export const delistItem = createAsyncThunk(
  'item/delistItem',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const encodedSku = encodeURIComponent(payload.sku)
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/items/${encodedSku}/delist`
        const response = await client.post(url, payload)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// checkItems - similar to getItems but doesn't affect Redux state
export const checkItems = createAsyncThunk(
  'item/checkItems',
  async (
    {
      sysSku,
      sku,
      shopUsername,
      branchCode,
      active,
      latest,
      approve,
      reject,
      delist,
      tags,
      itemCategory,
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      const state = getState()
      if (state.branch) {
        const queryParams = new URLSearchParams({
          ...(sysSku && { sysSku }),
          ...(sku && { sku }),
          ...(shopUsername && { shopUsername }),
          ...(branchCode && { branchCode }),
          ...(Array.isArray(tags) && tags.length > 0 && { tags: tags }),
          ...(itemCategory && { itemCategory }),
          ...(active !== undefined && { active }),
          ...(latest !== undefined && { latest }),
          ...(approve !== undefined && { approve }),
          ...(delist !== undefined && { delist }),
          ...(reject !== undefined && { reject }),
        })
        const url = `/shop/branches/${
          state.branch.currentSelectedBranchCode
        }/items?${queryParams.toString()}`
        const response = await client.get(url)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// getItemsForOrdering
export const getItemsForOrdering = createAsyncThunk(
  'item/getItemsForOrdering',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/items/order`
        const response = await client.get(url)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// updateItemOrder
export const updateItemOrder = createAsyncThunk(
  'item/updateItemOrder',
  async (itemOrders, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/items/order`
        const response = await client.put(url, { itemOrders })

        // Refresh items after updating order
        dispatch(getItems({ branchCode: state.branch.currentSelectedBranchCode }))
        // Also refresh items for ordering to update the modal data
        dispatch(getItemsForOrdering())

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// checkSkuAvailability - Check if SKU is available for use
export const checkSkuAvailability = createAsyncThunk(
  'item/checkSkuAvailability',
  async ({ sku, excludeSysSku }, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const queryParams = new URLSearchParams({
          sku,
          ...(excludeSysSku && { excludeSysSku }),
        })
        const url = `/shop/branches/${
          state.branch.currentSelectedBranchCode
        }/items/check-sku?${queryParams.toString()}`
        const response = await client.get(url)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Selector (Read data) - used with useSelector
export const selectItems = (state) => state.item.items
export const selectGetItemsStatus = (state) => state.item.getItems.status
export const selectGetItemsError = (state) => state.item.getItems.error
export const selectCreateItemStatus = (state) => state.item.createItem.status
export const selectCreateItemError = (state) => state.item.createItem.error
export const selectUpdateItemStatus = (state) => state.item.updateItem.status
export const selectUpdateItemError = (state) => state.item.updateItem.error
export const selectSetItemActiveStatusStatus = (state) => state.item.setItemActiveStatus.status
export const selectSetItemActiveStatusError = (state) => state.item.setItemActiveStatus.error
export const selectDelistItemStatus = (state) => state.item.delistItem.status
export const selectDelistItemError = (state) => state.item.delistItem.error
export const selectUpdateItemOrderStatus = (state) => state.item.updateItemOrder.status
export const selectUpdateItemOrderError = (state) => state.item.updateItemOrder.error
export const selectItemsForOrdering = (state) => state.item.getItemsForOrdering.data
export const selectGetItemsForOrderingStatus = (state) => state.item.getItemsForOrdering.status
export const selectGetItemsForOrderingError = (state) => state.item.getItemsForOrdering.error

export default itemSlice.reducer
